/**
 * Utilities for the transcript cue timecode format.
 *
 * Transcript cues store their timing as canonical SRT timecode strings
 * ("HH:MM:SS,mmm") — this is the source of truth. Frames are derived only at
 * the render boundary (see TranscriptElement). Because the format is
 * fixed-width and zero-padded, lexicographic string comparison is
 * chronologically monotonic, which lets callers (e.g. variation diffing)
 * compare cue timings with plain string equality/ordering — with no float
 * precision pitfalls and no lossy seconds↔frames round-trip.
 */

/**
 * Parse an SRT ("HH:MM:SS,mmm") or short ("MM:SS[,mmm]") timecode into seconds.
 * Tolerant of missing hours and missing milliseconds.
 */
export function srtTimecodeToSeconds(timecode: string): number {
    const [time = '0', ms = '0'] = String(timecode ?? '')
        .trim()
        .split(',');
    const parts = time.split(':').map(Number);
    // Read from the end so both "HH:MM:SS" and short "MM:SS" forms work.
    const seconds = parts.pop() || 0;
    const minutes = parts.pop() || 0;
    const hours = parts.pop() || 0;

    return hours * 3600 + minutes * 60 + seconds + (Number(ms) || 0) / 1000;
}

/** Format seconds into a canonical SRT timecode string ("HH:MM:SS,mmm"). */
export function secondsToSrtTimecode(seconds: number): string {
    const totalMs = Math.max(0, Math.round(seconds * 1000));
    const hours = Math.floor(totalMs / 3_600_000);
    const minutes = Math.floor((totalMs % 3_600_000) / 60_000);
    const secs = Math.floor((totalMs % 60_000) / 1000);
    const ms = totalMs % 1000;
    const pad = (v: number, size = 2) => String(v).padStart(size, '0');

    return `${pad(hours)}:${pad(minutes)}:${pad(secs)},${pad(ms, 3)}`;
}

/** Normalize any accepted timecode form into the canonical "HH:MM:SS,mmm" form. */
export function normalizeSrtTimecode(timecode: string): string {
    return secondsToSrtTimecode(srtTimecodeToSeconds(timecode));
}

/** Convert an SRT timecode string to a frame index at the given frame rate. */
export function srtTimecodeToFrame(timecode: string, frameRate: number): number {
    return Math.round(srtTimecodeToSeconds(timecode) * frameRate);
}
